"use client"

import { defaultLocale } from "@/i18n/request"
import { buildIdSlugRedirectPath, getSiteUrl } from "@repo/shared"
import IconFont from "@repo/ui/iconfont"
import { useLocale, useTranslations } from "next-intl"
import Link from "next/link"
import { useRouter } from "next/navigation"
import { useEffect, useRef, useState } from "react"

import Style from "./index.module.scss"

type Props = {
	html: string
	token: string
	reportId: number
	reportName: string
	hasSample: boolean
	hasAuth: boolean
}

const IntroCollapse = ({ html, token, reportId, reportName, hasSample, hasAuth }: Props) => {
	const [isExpanded, setIsExpanded] = useState(false)
	const [hasOverflow, setHasOverflow] = useState(false)
	/** 样本入口登录成功后，等待 refresh 带回 token + has_auth 再分支 */
	const pendingAfterLoginRef = useRef(false)
	const descRef = useRef<HTMLDivElement | null>(null)
	const locale = useLocale()
	const t = useTranslations("DetailPage.intro")
	const router = useRouter()
	const previewPath = buildIdSlugRedirectPath({
		locale,
		defaultLocale,
		id: reportId,
		title: reportName,
		routePrefix: "report-preview",
	})

	/** 已登录且在订阅有效期（品类 has_auth） */
	const hasFullAccess = Boolean(token && hasAuth)
	/** 有样本 且（未登录 或 已登录但不在订阅期） */
	const showViewSample = hasSample && !hasFullAccess

	useEffect(() => {
		const element = descRef.current
		if (!element || isExpanded) return

		const measureOverflow = () => {
			setHasOverflow(element.scrollHeight > element.clientHeight + 1)
		}
		measureOverflow()
		window.addEventListener("resize", measureOverflow)
		return () => window.removeEventListener("resize", measureOverflow)
	}, [html, isExpanded])

	useEffect(() => {
		if (!pendingAfterLoginRef.current || !token) return

		pendingAfterLoginRef.current = false
		if (hasAuth) {
			// 订阅期内：整页刷新，同步隐藏样本按钮并更新报告列表权限
			window.location.reload()
			return
		}
		router.push(previewPath)
	}, [hasAuth, previewPath, router, token])

	const handleLoginSuccess = () => {
		pendingAfterLoginRef.current = true
		router.refresh()
	}

	const handleViewSample = () => {
		if (token) {
			router.push(previewPath)
			return
		}

		window.SmmAuth?.showLogin({
			action: "login",
			language: locale,
			onLoginSuccess: handleLoginSuccess,
			onRegisterSuccess: handleLoginSuccess,
		})
	}

	return (
		<>
			{html && (
				<div className={Style.introWrapper}>
					<div
						ref={descRef}
						className={`${Style.desc} ${isExpanded ? Style.expanded : Style.collapsed}`}
						dangerouslySetInnerHTML={{ __html: html }}
					/>
					{hasOverflow && (
						<span className={Style.moreInline}>
							<button type="button" className={Style.more} onClick={() => setIsExpanded((value) => !value)}>
								{isExpanded ? t("less") : t("more")}
								<IconFont
									type="icon-xialacu"
									style={{
										fill: "#d7000f",
										fontSize: "20px",
										transform: isExpanded ? "rotate(180deg)" : "rotate(0deg)",
									}}
								/>
							</button>
						</span>
					)}
				</div>
			)}

			<div className={Style.actionRow}>
				<div className={Style.actionRowBtns}>
					<Link
						href={`${getSiteUrl("www")}/${locale}/about-us#contact-us`}
						target="_blank"
						className={Style.us}
						prefetch={false}
						rel="noopener noreferrer"
					>
						{t("contactUs")}
						<IconFont type="icon-a-Arrow-rightjiantouyou" style={{ fill: "#fff", fontSize: "12px" }} />
					</Link>

					{showViewSample && (
						<button type="button" className={Style.view} onClick={handleViewSample}>
							{t("viewReport")}
							<IconFont type="icon-a-Arrow-rightjiantouyou" style={{ fill: "#d7000f", fontSize: "12px" }} />
						</button>
					)}
				</div>

				{hasOverflow && (
					<button
						type="button"
						className={`${Style.more} ${Style.moreDesktop}`}
						onClick={() => setIsExpanded((value) => !value)}
					>
						{isExpanded ? t("less") : t("more")}
						<IconFont
							type="icon-xialacu"
							style={{
								fill: "#d7000f",
								fontSize: "20px",
								transform: isExpanded ? "rotate(180deg)" : "rotate(0deg)",
							}}
						/>
					</button>
				)}
			</div>
		</>
	)
}

export default IntroCollapse
